import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  HStack,
  Heading,
  Text,
  useToast,
} from '@chakra-ui/react';
import { LogOut, MonitorSmartphone } from 'lucide-react';
import type { SignedInSession } from '@twinden/shared';
import { SettingRow } from './page/PageScaffold';
import { ConfirmSheet } from './ConfirmSheet';
import { useAuth, useLoadingState } from '../hooks';
import { LoadingPanel } from './brand/TwindenLoader';
import { api } from '../lib/api';
import { confirmItsYou, deviceErrorMessage } from '../lib/devices';
import { sessionHow, sessionName } from '../lib/sessions';
import { appLocale } from '../lib/format';
import { useT } from '../i18n';

type Pending =
  | { kind: 'one'; session: SignedInSession }
  | { kind: 'everywhereElse' }
  | null;

/**
 * Profile → My devices → Signed-in sessions (D214): every device or browser
 * that can still sign in silently, with Sign out on each and "Sign out
 * everywhere else". Signing out another device asks for a passkey check
 * (D213); this device is an ordinary sign-out.
 */
export function SignedInSessions({ refreshKey = 0 }: { refreshKey?: number }) {
  const t = useT();
  const toast = useToast();
  const { logout } = useAuth();
  const [sessions, setSessions] = useState<SignedInSession[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await api.getMySessions();
      setSessions(response.data);
      setFailed(false);
    } catch {
      setFailed(true);
      setSessions((current) => current ?? []);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);
  // The small mark while the sessions load (M66, D250).
  const sessionsState = useLoadingState({ loading: sessions === null });

  // New-device notices open /profile#sessions (D94, D214).
  const { hash } = useLocation();
  const loaded = sessions !== null;
  useEffect(() => {
    if (loaded && hash === '#sessions') {
      document.getElementById('sessions')?.scrollIntoView?.({ block: 'start' });
    }
  }, [loaded, hash]);

  const day = (iso: string) =>
    new Date(iso).toLocaleDateString(appLocale(), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  const current = sessions?.find((s) => s.current) ?? null;
  const others = sessions?.filter((s) => !s.current) ?? [];
  // A review session can only sign itself out (D213).
  const isReview = current?.method === 'review';

  const confirm = async () => {
    const action = pending;
    if (!action) return;
    setBusy(true);
    try {
      if (action.kind === 'one' && action.session.current) {
        setPending(null);
        await logout();
        return;
      }
      const verification = await confirmItsYou();
      const revoked =
        action.kind === 'one'
          ? (await api.revokeSession(action.session.id, verification)).data
              .revoked
          : (await api.revokeOtherSessions(verification)).data.revoked;
      setPending(null);
      toast({
        title:
          revoked === 1
            ? t('account.sessions.signedOutOne')
            : t('account.sessions.signedOutOther', { count: revoked }),
        status: 'success',
      });
      await load();
    } catch (err) {
      const message = deviceErrorMessage(err);
      if (message) {
        toast({
          title: t('account.sessions.failed'),
          description: message,
          status: 'error',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const pendingName =
    pending?.kind === 'one' ? sessionName(pending.session, t) : '';

  return (
    <Box id="sessions" pt={4} data-testid="signed-in-sessions">
      <Heading as="h3" fontSize="17px" mb={1}>
        {t('account.sessions.title')}
      </Heading>
      <Text fontSize="14px" color="muted" mb={2}>
        {t('account.sessions.description')}
      </Text>
      {sessions === null || sessionsState.show ? (
        <LoadingPanel state={sessionsState} size="inline" />
      ) : (
        <>
          {failed && (
            <Text color="danger" fontSize="14px" py={2}>
              {t('account.sessions.loadFailed')}
            </Text>
          )}
          {[...(current ? [current] : []), ...others].map((session) => {
            const name = sessionName(session, t);
            return (
              <SettingRow
                key={session.id}
                label={
                  <HStack as="span" spacing={2} flexWrap="wrap">
                    <MonitorSmartphone size={16} aria-hidden="true" />
                    <span>{name}</span>
                    {session.current && (
                      <Badge
                        bg="accent.tint"
                        color="accent"
                        borderRadius="full"
                        px={2}
                        textTransform="none"
                        fontSize="12px"
                      >
                        {t('account.sessions.thisDevice')}
                      </Badge>
                    )}
                  </HStack>
                }
                hint={
                  <>
                    {sessionHow(session, t)}
                    <br />
                    {session.lastUsedAt
                      ? t('account.sessions.times', {
                          first: day(session.createdAt),
                          used: day(session.lastUsedAt),
                        })
                      : t('account.sessions.firstSeen', {
                          first: day(session.createdAt),
                        })}
                  </>
                }
              >
                {(session.current || !isReview) && (
                  <Button
                    h="40px"
                    variant="outline"
                    colorScheme="gray"
                    borderColor="line"
                    aria-label={t('account.sessions.signOutNamed', { name })}
                    onClick={() => setPending({ kind: 'one', session })}
                  >
                    {t('account.sessions.signOut')}
                  </Button>
                )}
              </SettingRow>
            );
          })}
          {current && others.length === 0 && (
            <Text fontSize="14px" color="muted" pt={1}>
              {t('account.sessions.onlyThis')}
            </Text>
          )}
          {others.length > 0 && !isReview && (
            <Button
              mt={3}
              w="100%"
              variant="outline"
              colorScheme="gray"
              borderColor="line"
              color="danger"
              leftIcon={<LogOut size={18} />}
              onClick={() => setPending({ kind: 'everywhereElse' })}
            >
              {t('account.sessions.everywhereElse')}
            </Button>
          )}
        </>
      )}

      <ConfirmSheet
        isOpen={pending !== null}
        onClose={() => !busy && setPending(null)}
        icon={<LogOut size={28} />}
        centered
        danger
        title={
          pending?.kind === 'everywhereElse'
            ? t('account.sessions.everywhereElseTitle')
            : pending?.session.current
              ? t('account.sessions.signOutThisTitle')
              : t('account.sessions.signOutTitle', { name: pendingName })
        }
        body={
          pending?.kind === 'everywhereElse'
            ? t('account.sessions.everywhereElseBody')
            : pending?.session.current
              ? t('account.sessions.signOutThisBody')
              : t('account.sessions.signOutBody')
        }
        confirmLabel={
          pending?.kind === 'everywhereElse'
            ? t('account.sessions.everywhereElse')
            : t('account.sessions.signOut')
        }
        cancelLabel={t('account.sessions.keep')}
        isLoading={busy}
        onConfirm={() => void confirm()}
      />
    </Box>
  );
}
